import { useContext, useEffect, useRef, useState } from "react";

import { fetchGpt4API } from "../../services/apiService";
import { extractJSONFromString } from "../../utils/helpers";
import { DiscoveryContext } from "./discovery-context";

export default function useTopUserProducts() {
  const [loading, setLoading] = useState(false);
  const [keyPlayers, setKeyPlayers] = useState({});
  const discoveryCtx = useContext(DiscoveryContext);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  function generatePrompt(
    category,
    type = "product",
    userRole = "General Consumer",
    attributes = [],
    country = "United States",
    exclude = ""
  ) {
    const attributesSection = attributes.length
      ? `and prioritize ${type}s that align with attributes users care about, such as: ${attributes.join(
        ", "
      )}.`
      : "";

    const exclusionSection = exclude.trim()
      ? type.toLowerCase() === "brand"
        ? `Exclude the brand "${exclude}" from the results.`
        : `Exclude products from the brand/company "${exclude}" from the results.`
      : "";

    const typeSpecificContent = {
      product: {
        rankingCriteria: "popularity or market share",
        jsonExample:
          '{ "name": "[Brand] [Model]", "category": "[Category]", "domain": "url.com", "variants": ["Variant 1", "Variant 2", "Variant 3"] }',
        specificExample:
          'If the category is "EV Pickup Trucks," an example product could be:\n            { "name": "Tesla Cybertruck", "category": "EV Pickup Trucks", "domain": "tesla.com", "variants": ["Tesla Cybertruck Dual Motor", "Tesla Cybertruck Tri Motor", "Tesla Cybertruck Foundation Series"] }',
        additionalExample:
          'If the category is "Sedans," and the user cares about "Performance" and "Fuel Efficiency," the rankings should prioritize products that excel in these areas while maintaining market relevance',
      },
      brand: {
        rankingCriteria: "brand value, market presence, and consumer trust",
        jsonExample: '{ "name": "[Brand Name]", "domain": "url.com" }',
        specificExample:
          'If the category is "Automotive," an example brand could be:\n            { "name": "Tesla", "domain": "tesla.com" }',
        additionalExample:
          'If the category is "Automotive," and the user cares about "Innovation" and "Sustainability," the rankings should prioritize brands that are known for these attributes while maintaining market relevance',
      },
    };

    const content = typeSpecificContent[type.toLowerCase()];

    return `
        Based on the specified category "${category}" and market in "${country}", provide a list of the top 8 ${type}s that belong to this category, ranked in order of ${content.rankingCriteria
      }.  
        Consider the user's role as "${userRole}" ${attributesSection}
        ${exclusionSection} 
    
        ### Instructions:  
        - Output the response in the following JSON format:  
        {
          "data": [
            ${content.jsonExample},
            ${content.jsonExample},
            ${content.jsonExample},
            ...
          ]
        }
        ${type.toLowerCase() === "product"
        ? `- Each product name should strictly follow the format: **"[Brand] [Model]"**.
          - **Do not include any variant or extended series information in the product "name".**  
            - Example: If the full product name includes a variant (e.g., "Tesla Cybertruck Dual Motor"), the "name" should be **"Tesla Cybertruck"** only.
          - **Strict Rule for Smartphones:**  
            - **Do NOT include storage options (e.g., "128GB", "256GB") in product names or variants.**
            - ✅ **Correct Variant Example:**  
              { "name": "iPhone 14", "category": "Smartphones", "domain": "apple.com", "variants": ["iPhone 14 Pro", "iPhone 14 Pro Max"] }  
            - ❌ **Incorrect Variant Example:**  
              { "name": "iPhone 14", "category": "Smartphones", "domain": "apple.com", "variants": ["iPhone 14 128GB", "iPhone 14 256GB"] }  
          - If variant information is applicable, it should be returned solely in the "variants" array.
            - Example:
              {
                "name": "Ford F-150 Lightning",
                "category": "EV Pickup Trucks",
                "domain": "ford.com",
                "variants": ["Ford F-150 Lightning Platinum", "Ford F-150 Lightning XLT", "Ford F-150 Lightning Lariat"]
              }
            - If the user input already specifies a variant (e.g., "Nike Air Max 2017"), return an empty array for "variants".
          - Each product must belong to the specified category: **"${category}"**.
          `
        : `- Each brand name should strictly follow the format: **"[Brand Name]"**.
          - Do not include product names or model/series names for brands.`
      }
        - Each ${type} should have its official website domain listed in the "domain" field.
        ${content.specificExample ? `- ${content.specificExample}` : ""}
        - Rankings should reflect the user's role and the listed attributes while still accounting for overall ${content.rankingCriteria
      } in ${country}.
        - If no internet access is available, use existing knowledge to determine the rankings and attributes.
        ${exclusionSection ? `- ${exclusionSection}` : ""}
    
        ### Considerations:  
        - Only include ${type}s that fit within the specified **"${category}"**.  
        - For users with roles like "Home Maker," focus on ${type}s with ease of use or practicality.  
        - For roles like "Salesperson," emphasize performance, style, or features enhancing productivity.
        - Consider ${type}s that are officially available in ${country}.
        - Account for local market preferences and regulations in ${country}.
        - Ensure accuracy and relevance to the specified category.
        - Do not include placeholders or incomplete fields in the output.  
    
        ### Additional Example:
        ${content.additionalExample} in ${country}.  
      `;
  }


  useEffect(() => {
    if (discoveryCtx.abort_launch) {
      // setKeyPlayers({});
    }
  }, [discoveryCtx.abort_launch]);

  async function getBrandOrProductSuggestions(type, category, occupation, attributes, location, market, entity) {
    console.log("getBrandOrProductSuggestions", type, category, occupation, attributes, location, market, entity);
    const requestId = ++requestIdRef.current;

    //if (abortControllerRef.current) {
    //  abortControllerRef.current.abort();
    // }

    // abortControllerRef.current = new AbortController();
    const abortController = new AbortController();

    setLoading(true);
    const prompt = generatePrompt(
      category,
      type,
      occupation,
      attributes,
      type === "category"
        ? location
        : market,
      entity
    );
    try {
      console.log("getBrandOrProductSuggestions request",);
      let response = await fetchGpt4API(
        prompt,
        abortController,
        1000,
        1,
        "gpt-4o"
      );
      console.log("getBrandOrProductSuggestions response", response);
      response = await extractJSONFromString(response["GPT-4"][0]);
      if (response && response?.data) {
        const fetchedKeyPlayers = response?.data;
        // if (requestId === requestIdRef.current) {
        console.log(" getBrandOrProductSuggestions fetchedKeyPlayers", keyPlayers, fetchedKeyPlayers, category);
        // setKeyPlayers({...keyPlayers, [category]:
        //   fetchedKeyPlayers.map((player) => {
        //     return {
        //       ...player,
        //       variants: player?.variants
        //         ? player?.variants.map((variant) => {
        //             return {
        //               id: variant,
        //               name: variant,
        //               active: true,
        //             };
        //           })
        //         : [],
        //       display_name: `${player.name}${
        //         player?.variants?.length
        //           ? `(${player?.variants?.length} variants)`
        //           : ""
        //       }`,
        //     };
        //   })
        // });
        // }
        setKeyPlayers((prev) => ({
          ...prev,
          [category]: fetchedKeyPlayers.map((player) => ({
            ...player,
            variants: player?.variants
              ? player?.variants.map((variant) => ({
                id: variant,
                name: variant,
                active: true,
              }))
              : [],
            display_name: `${player.name}${player?.variants?.length ? `(${player?.variants?.length} variants)` : ""
              }`,
          })),
        }));

      } else {
        console.error("getBrandOrProductSuggestions error ", response);

      }
    } catch (error) {
      console.error("getBrandOrProductSuggestions error ", error);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }

  return { keyPlayers, setKeyPlayers, loading, getBrandOrProductSuggestions };
}
